import React, { PropsWithChildren, createContext, useContext, useState } from 'react';
import { ErpStatsResponse } from 'com.cogvio.pricemonitor.api';
import { ERPSortBy } from '~/libs/infrastructure/schemas/erp';

export type BarSeriesValue = [string, number | null];

type CountryLike = { alpha2: string };

const sortCountryACountryB = (a: CountryLike, b: CountryLike) => a.alpha2.localeCompare(b.alpha2);

export function sortCountries<T extends CountryLike>(countries: T[]) {
	return countries.sort((country, otherCountry) => sortCountryACountryB(country, otherCountry));
}

export function sortCountryEntities<T extends { country: CountryLike }>(entitiesWithCountry: T[]) {
	return entitiesWithCountry.sort((a, b) => sortCountryACountryB(a.country, b.country));
}

export function sortSeriesByValue(a: BarSeriesValue, b: BarSeriesValue) {
	return (a[1] ?? 0) - (b[1] ?? 0);
}

export function sortSeriesByName(a: BarSeriesValue, b: BarSeriesValue) {
	return a[0].localeCompare(b[0]);
}

function useChartSort() {
	const [sortBy, setSortBy] = useState(ERPSortBy.PRICE);

	const handleSortChange = () => {
		setSortBy(sortBy === ERPSortBy.PRICE ? ERPSortBy.NAME : ERPSortBy.PRICE);
	};

	const sortSeries = (series: BarSeriesValue[]) =>
		series.sort(sortBy === ERPSortBy.PRICE ? sortSeriesByValue : sortSeriesByName);

	const sortCategories = (series: BarSeriesValue[], stats: ErpStatsResponse) =>
		sortBy === ERPSortBy.PRICE
			? [...series.reverse()].map((item) => item[0])
			: sortCountryEntities(stats.quadrants).map(({ country }) => country.alpha2);

	return { sortBy, handleSortChange, sortSeries, sortCategories };
}

interface SortingContextProps {
	sortBy: ERPSortBy;
	handleSortChange: () => void;
	sortSeries: (series: BarSeriesValue[]) => BarSeriesValue[];
	sortCategories: (series: BarSeriesValue[], stats: ErpStatsResponse) => string[];
}

const SortingContext = createContext<SortingContextProps>({
	sortBy: ERPSortBy.PRICE,
	handleSortChange: () => void 0,
	sortSeries: () => [],
	sortCategories: () => [],
});

export function SortingContextProvider({ children }: PropsWithChildren<Record<never, never>>) {
	const sort = useChartSort();
	return <SortingContext.Provider value={sort}>{children}</SortingContext.Provider>;
}

export function useSortingContext() {
	return useContext(SortingContext);
}
